import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row,
  Col,
  ListGroup,
  Button,
  Card,
  Form,
  Accordion,
} from 'react-bootstrap';
import {
  FaShoppingCart,
  FaBox,
  FaCashRegister,
  FaUsers,
  FaStickyNote,
  FaTrash,
  FaFileExcel,
} from 'react-icons/fa';
import * as XLSX from 'xlsx';
import Message from '../components/Message';
import { addToCart, removeFromCart } from '../slices/cartSlice';
import { useGetProductsQuery } from '../slices/productsApiSlice';


const StoreCartForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cartItems } = useSelector((state) => state.cart);


  const { data } = useGetProductsQuery({ pageNumber: 1 });
  const productList = data?.products || [];

  // -------------------------------
  // Excel Upload
  // -------------------------------
  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || productList.length === 0) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(new Uint8Array(evt.target.result), { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet);

      rows.forEach((row) => {
        const name = row.name?.trim();
        const qty = parseInt(row.qty, 10);

        if (!name || isNaN(qty)) return;

        const matchedProduct = productList.find(
          (p) => p.name.trim().toLowerCase() === name.toLowerCase()
        );

        if (matchedProduct) {
          dispatch(
            addToCart({
              _id: matchedProduct._id,
              name: matchedProduct.name,
              price: matchedProduct.price,
              qty,
              countInStoreStock: matchedProduct.countInStoreStock,
              images: matchedProduct.images || [],
            })
          );
        }
      });
    };
    reader.readAsArrayBuffer(file);
  };

  // -------------------------------
  // Export Cart to Excel
  // -------------------------------
  const exportCartToExcel = () => {
    const data = cartItems.map((item) => ({
      _id: item._id,
      name: item.name,
      price: item.price,
      qty: item.qty,
      total: item.price * item.qty,
      countInStoreStock: item.countInStoreStock,
      image: item.images?.[0] || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'MiniCartItems');
    XLSX.writeFile(workbook, 'MiniCartItems.xlsx');
  };

  // -------------------------------
  // Categories
  // -------------------------------
  const categories = useMemo(
    () => ({
      'Forms & Pages': {
        color: '#007bff',
        icon: <FaShoppingCart className="me-2" />,
        items: {
          storeSalesform: 'SaleForm',
          storeStockform: 'StockForm',
          dailystoreStocksform: 'DailyStockForm',
          StoreSupplyForm: 'SupplyForm',
          StoreTransferForm: 'TransferForm',
          staffstoreSale: 'Sale',
          staffstoreSupply: 'Supply',
          staffstoreStock: 'Stock',
          staffstoreTransfer: 'Transfer',
          staffdailystoreStock: 'DailyStock',
          openingstoreStocksform: 'OpeningStockForm',
          staffopeningstoreStock: 'OpeningStock',
        },
      },

      'Mini Balances & Reports': {
        color: '#6f42c1',
        icon: <FaStickyNote className="me-2" />,
        items: {
          storeBalancescreen: 'BalanceScreen',
          storeBalancebyuserscreen: 'BalanceByUser',
          storeBalancebycustomerscreen: 'BalanceByCustomer',
          'storeAdmin/useStoreStockAnalysis': 'StockAnalysis',
          
        },
      },

      'Mini Income & Expense': {
        color: '#ffc107',
        icon: <FaStickyNote className="me-2" />,
        items: {
          storeExpenselist: 'ExpenseList',
        },
      },

      'Mini Cash & Bank': {
        color: '#17a2b8',
        icon: <FaCashRegister className="me-2" />,
        items: {
          StoreCashUserList: 'Cash Collected',
          StoreBankUserList: 'BankData',
        },
      },

      'Mini Products & Inventory': {
        color: '#28a745',
        icon: <FaBox className="me-2" />,
        items: {
          productlist: 'Itemlist',
        },
      },

      'Customers & Suppliers': {
        color: '#fd7e14',
        icon: <FaUsers className="me-2" />,
        items: {
          customeruserlist: 'MiniCustomerlist',
          supplierlist: 'Supplierlist',
        },
      },

      'Mini Billing': {
        color: '#6610f2',
        icon: <FaStickyNote className="me-2" />,
        items: {
          staffbill: 'MiniFinalInsuBill',
        },
      },
    }),
    []
  );

  // -------------------------------
  // Search
  // -------------------------------
  const [search, setSearch] = useState('');

  const filteredCategories = useMemo(() => {
    const result = {};

    Object.entries(categories).forEach(([catName, catData]) => {
      const matched = Object.entries(catData.items).filter(([path, label]) =>
        label.toLowerCase().includes(search.toLowerCase())
      );

      if (matched.length > 0) {
        result[catName] = {
          ...catData,
          items: Object.fromEntries(matched),
        };
      }
    });

    return result;
  }, [search, categories]);

  const nav = (path) => navigate(`/login?redirect=/${path}`);

  return (
    <Row>
      {/* LEFT PANEL */}
      <Col md={8}>
        <h1 className="mb-4">Browse Mini Forms/Pages</h1>

        <Form.Group controlId="excelUpload" className="mb-3">
          <Form.Label>Upload Excel File</Form.Label>
          <Form.Control type="file" accept=".xlsx, .xls" onChange={handleExcelUpload} />
        </Form.Group>

        {cartItems.length === 0 ? (
          <Message>
            Go <Link to="/house/HomeScreen">Back</Link>
          </Message>
        ) : (
          <ListGroup variant="flush">
            {cartItems.map((item) => (
              <ListGroup.Item key={item._id}>
                <Row className="align-items-center">
                  <Col md={2}>
                    {item.images?.length > 0 && (
                      <img
                        src={item.images[0]}
                        alt="Thumbnail"
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </Col>

                  <Col md={3}>
                    <Link to={`/product/${item._id}`}>{item.name}</Link>
                  </Col>

                  <Col md={2}>${item.price}</Col>

                  <Col md={2}>
                    <Button
                      variant="light"
                      onClick={() =>
                        dispatch(addToCart({ ...item, qty: item.qty - 1 }))
                      }
                      disabled={item.qty === 1}
                    >
                      -
                    </Button>
                    <span className="mx-2">{item.qty}</span>
                    <Button
                      variant="light"
                      onClick={() =>
                        dispatch(addToCart({ ...item, qty: item.qty + 1 }))
                      }
                      disabled={item.qty === item.countInStoreStock}
                    >
                      +
                    </Button>
                  </Col>

                  <Col md={2}>${(item.price * item.qty).toFixed(2)}</Col>

                  <Col md={1}>
                    <Button variant="light" onClick={() => dispatch(removeFromCart(item._id))}>
                      <FaTrash />
                    </Button>
                  </Col>
                </Row>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Col>

      {/* RIGHT PANEL */}
      <Col md={4}>
        <Card>
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>
                Subtotal ({cartItems.reduce((acc, item) => acc + item.qty, 0)}) items
              </h2>
              ${cartItems.reduce((acc, item) => acc + item.qty * item.price, 0).toFixed(2)}
            </ListGroup.Item>

            {/* SEARCH BAR */}
            <ListGroup.Item>
              <Form.Control
                type="text"
                placeholder="Search Mini pages..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </ListGroup.Item>

            {/* COLLAPSIBLE SECTIONS */}
            <ListGroup.Item>
              <Accordion alwaysOpen>
                {Object.entries(filteredCategories).map(
                  ([catName, { color, icon, items }], idx) => (
                    <Accordion.Item eventKey={idx.toString()} key={catName}>
                      <Accordion.Header>
                        <span style={{ color }}>{icon}{catName}</span>
                      </Accordion.Header>

                      <Accordion.Body>
                        <Row>
                          {Object.entries(items).map(([path, label]) => (
                            <Col md={6} className="mb-2" key={path}>
                              <Button
                                className="w-100"
                                style={{
                                  backgroundColor: color,
                                  borderColor: color,
                                }}
                                disabled={
                                  catName === 'Mini Sales & Purchases' &&
                                  cartItems.length === 0
                                }
                                onClick={() => nav(path)}
                              >
                                {label}
                              </Button>
                            </Col>
                          ))}
                        </Row>
                      </Accordion.Body>
                    </Accordion.Item>
                  )
                )}
              </Accordion>
            </ListGroup.Item>

            {/* EXPORT */}
            {cartItems.length > 0 && (
              <ListGroup.Item>
                <Button className="w-100" onClick={exportCartToExcel}>
                  <FaFileExcel className="me-2" />
                  Download Mini Cart to Excel
                </Button>
              </ListGroup.Item>
            )}
          </ListGroup>
        </Card>
      </Col>
    </Row>
  );
};

export default StoreCartForm;